import React, { useCallback, useMemo, useState } from 'react'
import { generatePath, Link } from 'react-router-dom'
import Table from 'antd/lib/table'

import apiManager from 'helpers/apiManager'
import useQuery from 'hooks/useQuery'
import { ROUTES } from 'helpers/constants'
import { dateFormatter } from 'helpers'

import ProfileImage from 'components/ProfileImage'
import SearchForm from 'pages/admin/AdminActiveUsers/SearchForm'
import Button from 'components/Button'
import Spinner from 'components/Spinner'
import DarkPrimaryButton from 'components/DarkPrimaryButton'

const getSorter =
  ({ key, formatter = data => data }) =>
  (a, b) =>
    formatter(a[key]) - formatter(b[key])

const getStringSorter =
  ({ key }) =>
  (a, b) =>
    a[key].localeCompare(b[key])

const ProfileLink = ({ user }) => {
  const publicProfilePath = useMemo(
    () => generatePath(ROUTES.ADMIN_USER, { id: user.username || user.id }),
    [user]
  )

  return (
    <Link to={publicProfilePath} target="_blank" className="flex items-center">
      <ProfileImage
        className="w-10 h-10 object-cover object-top rounded-full mr-2"
        src={user.profileImage}
      />
      {user.name}
    </Link>
  )
}

const ProfileAI = ({ user }) => {
  const path = useMemo(() => generatePath(ROUTES.ADMIN_USER_AI, { id: user.id }), [user])

  return (
    <Link to={path} target="_blank" className="">
      AI
    </Link>
  )
}

const ActiveUsers = props => {
  const { action } = props
  const [query, setQuery] = useState({})
  const { isFetching, data, refetch } = useQuery(
    'admin/users/active',
    () =>
      apiManager.adminGetActiveUsers({
        search: query.search || undefined,
        filters: query.filters ? JSON.stringify(query.filters) : undefined,
      }),
    { refetchOnWindowFocus: false }
  )
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        sorter: getSorter({ key: 'id' }),
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        sorter: getStringSorter({ key: 'name' }),
        render: (_, user) => <ProfileLink user={user} />,
      },
      {
        title: 'Age',
        dataIndex: 'birthdate',
        key: 'birthdate',
        render: text => dateFormatter.getAgeFromBirthdate(text),
      },
      {
        title: 'Gender',
        dataIndex: 'gender',
        key: 'gender',
      },
      {
        title: 'Pref.',
        dataIndex: 'genderPreference',
        key: 'genderPreference',
      },
      {
        title: 'hNum',
        dataIndex: 'hNum',
        key: 'hNum',
      },
      {
        title: 'Approved',
        dataIndex: 'approvedAt',
        key: 'approvedAt',
        render: text => (text ? dateFormatter.returnYYYYMMDD(text) : ''),
        sorter: getSorter({ key: 'approvedAt', formatter: data => (data ? new Date(data) : 0) }),
      },
      {
        title: 'AI',
        dataIndex: 'id',
        key: 'ai',
        render: (_, user) => <ProfileAI user={user} />,
      },
      ...(action
        ? [
            {
              title: action.title,
              dataIndex: 'id',
              key: 'action',
              render: (_, user) => (
                <DarkPrimaryButton loading={action.isLoading} onClick={() => action.onAction(user)}>
                  {action.cta}
                </DarkPrimaryButton>
              ),
            },
          ]
        : []),
    ],
    [action]
  )

  const users = useMemo(
    () =>
      data?.map(user => ({
        ...user,
        key: user.id,
      })) || [],
    [data]
  )

  const handleSubmit = useCallback(
    query => {
      setQuery(query)
      setTimeout(() => refetch(), 100)
    },
    [refetch]
  )

  const handleReset = useCallback(() => {
    handleSubmit({})
    setIsSearchModalOpen(false)
  }, [handleSubmit])

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-7">
        <h1 className="text-4xl text-center">Active Users</h1>
        <div className="flex">
          <Button className="bg-red-500 mr-4" onClick={handleReset}>
            Reset filters
          </Button>
          <Button className="bg-azureBlue" onClick={() => setIsSearchModalOpen(true)}>
            Search for users
          </Button>
        </div>
      </div>
      {isSearchModalOpen && (
        <SearchForm
          query={query}
          onClose={() => setIsSearchModalOpen(false)}
          onSubmit={handleSubmit}
        />
      )}
      <div>
        {isFetching ? (
          <div className="h-72 w-full flex items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={users}
            pagination={{
              defaultPageSize: 100,
            }}
          />
        )}
      </div>
    </div>
  )
}

export default ActiveUsers
